import React from "react";
import { Check, Phone, Plus, ShieldCheck } from "lucide-react";
import LoadingCard from "../common/LoadingCard";
import EmptyCard from "../common/EmptyCard";

function PhoneNumberSection({
  phoneNumbers,
  selectedPhoneId,
  loading,
  workspaceId,
  accountId,
  onSelect,
  onAdd,
}) {
  return (
    <div className="h-full rounded-[22px] border border-[#d8e7ed] bg-white p-4 shadow-[0_14px_40px_rgba(15,43,66,0.06)] sm:p-5 dark:border-dark-border dark:bg-dark-card">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#cfe4ed] bg-[#f3f9fc] text-[#47758f] sm:h-10 sm:w-10 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary">
            <Phone size={16} strokeWidth={1.8} className="sm:hidden" />

            <Phone size={18} strokeWidth={1.8} className="hidden sm:block" />
          </div>

          <div className="min-w-0">
            <h3 className="truncate text-[11px] font-semibold text-text-primary sm:text-xs dark:text-dark-text-primary">
              Phone Number
            </h3>

            <p className="mt-0.5 truncate text-[9px] text-text-muted sm:text-[10px] dark:text-dark-text-muted">
              Messaging number
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <ShieldCheck
            size={14}
            strokeWidth={1.8}
            className="text-primary sm:h-4 sm:w-4 dark:text-dark-primary"
          />

          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              disabled={!workspaceId || !accountId || !onAdd}
              aria-label="Add phone number"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#d8e7ed] text-[#607787] transition-all hover:border-[#9bcbe2] hover:bg-[#edf8fd] hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-40 dark:border-dark-border dark:text-dark-text-secondary dark:hover:border-dark-primary/45 dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary"
            >
              <Plus size={13} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingCard small />
      ) : phoneNumbers.length === 0 ? (
        <>
          <EmptyCard text="No phone numbers found for this account." />

          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              disabled={!workspaceId || !accountId || !onAdd}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-theme-200 bg-theme-50 px-3 py-2.5 text-[9px] font-semibold text-primary transition-all hover:border-theme-300 hover:bg-theme-100 disabled:cursor-not-allowed disabled:opacity-40 sm:gap-2 sm:text-[10px] dark:border-dark-border dark:bg-dark-surface dark:text-dark-primary dark:hover:border-dark-primary/40 dark:hover:bg-dark-soft-blue"
            >
              <Plus size={12} strokeWidth={1.8} />
              Connect phone number
            </button>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {phoneNumbers.map((phone) => {
            const selected = String(phone.id) === String(selectedPhoneId);

            return (
              <button
                key={phone.id}
                type="button"
                onClick={() => onSelect(phone.id)}
                className={[
                  "flex w-full min-w-0 items-center gap-2.5 rounded-xl border p-2.5 text-left transition-all duration-200 sm:gap-3 sm:p-3",
                  selected
                    ? "border-[#9bcbe2] bg-[#f1f9fd] shadow-sm dark:border-dark-primary/50 dark:bg-dark-soft-blue"
                    : "border-[#e2ebef] bg-white hover:border-[#c9dce4] hover:bg-[#f8fbfc] dark:border-dark-border dark:bg-dark-surface dark:hover:border-dark-primary/40 dark:hover:bg-dark-blue-bg",
                ].join(" ")}
              >
                {/* Phone Icon */}
                <div
                  className={[
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors sm:h-9 sm:w-9",
                    selected
                      ? "bg-[#147db5] text-white shadow-[0_5px_14px_rgba(20,125,181,0.18)] dark:bg-dark-primary"
                      : "bg-[#edf3f6] text-[#607787] dark:bg-dark-card dark:text-dark-text-secondary",
                  ].join(" ")}
                >
                  <Phone size={14} strokeWidth={1.8} className="sm:hidden" />

                  <Phone
                    size={16}
                    strokeWidth={1.8}
                    className="hidden sm:block"
                  />
                </div>

                {/* Phone Details */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[10px] font-semibold text-text-primary sm:text-xs dark:text-dark-text-primary">
                    {phone.verified_name ||
                      phone.display_name ||
                      "WhatsApp Business"}
                  </p>

                  <p className="mt-0.5 truncate text-[8px] text-text-muted sm:text-[10px] dark:text-dark-text-muted">
                    {phone.display_phone_number ||
                      phone.phone_number ||
                      phone.number ||
                      "Phone number"}
                  </p>
                </div>

                {/* Status + Selected */}
                <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                  {phone.status && (
                    <span className="hidden rounded-full bg-theme-100 px-2 py-0.5 text-[9px] font-semibold text-primary sm:block dark:bg-dark-soft-blue dark:text-dark-primary">
                      {phone.status}
                    </span>
                  )}

                  {selected && (
                    <Check
                      size={14}
                      strokeWidth={2}
                      className="text-[#147db5] sm:h-[15px] sm:w-[15px] dark:text-dark-primary"
                    />
                  )}
                </div>
              </button>
            );
          })}

          {/* Add Another Number */}
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              disabled={!workspaceId || !accountId || !onAdd}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#c9dce4] px-3 py-2.5 text-[9px] font-semibold text-[#607787] transition-all hover:border-[#9bcbe2] hover:bg-[#edf8fd] hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-40 sm:gap-2 sm:text-[10px] dark:border-dark-border dark:text-dark-text-secondary dark:hover:border-dark-primary/45 dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary"
            >
              <Plus size={12} strokeWidth={1.8} />
              Connect another number
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default PhoneNumberSection;
